import React, {Fragment} from 'react';
import {PageMetaTags} from '@common/http/page-meta-tags';
import {PageStatus} from '@common/http/page-status';
import {SitePageLayout} from '@app/site-page-layout';
import {
  GetNewsArticleResponse,
  useNewsArticle,
} from '@app/admin/news/requests/use-news-article';
import {NewsArticle} from '@app/titles/models/news-article';
import {Trans} from '@common/i18n/trans';
import {FormattedDate} from '@common/i18n/formatted-date';
import {BulletSeparatedItems} from '@app/titles/bullet-separated-items';
import {NewsArticleImage} from '@app/news/news-article-image';
import {NewsArticleLink} from '@app/news/news-article-link';
import {NewsArticleByline} from '@app/news/news-article-byline';
import {NewsArticleSourceLink} from '@app/news/news-article-source-link';
import {Col, Container, Row} from 'react-bootstrap';

export function NewsArticlePage() {
  const query = useNewsArticle('newsArticlePage');

  const content = query.data ? (
    <Fragment>
      <PageMetaTags query={query} />
      <PageContent data={query.data} />
    </Fragment>
  ) : (
    <PageStatus query={query} loaderClassName="absolute inset-0 m-auto" />
  );

  return <SitePageLayout>{content}</SitePageLayout>;
}

interface PageContentProps {
  data: GetNewsArticleResponse;
}
function PageContent({data: {article, related}}: PageContentProps) {
  return (
    <>
      <div
        className="iq-breadcrumb"
        style={{
          backgroundImage: `url(https://pitcch.eu/wp-content/themes/pitcch/dist/images/banner-pages-bg.jpg)`,
        }}
      >
        <Container className="w-full" fluid>
          <Row className="align-items-center">
            <Col sm="12">
              <nav className="text-center">
                <h2 className={`title capitalize`}>{article.title}</h2>
              </nav>
            </Col>
          </Row>
        </Container>
      </div>
      <div className="container mt-20 items-start gap-40  text-white lg:flex">
        <main className="mb-24 flex-auto rounded  p-16">
          <NewsArticleImage
            article={article}
            size="w-184 h-184"
            className="mb-24"
          />
          <div className="iq-blog-meta mb-3 flex flex-col gap-4 md:flex-row">
            <ul className="iq-blogtag list-inline mb-0">
              <li className="border-gredient-left p-2">
                <FormattedDate date={article.created_at} />
              </li>
            </ul>
            <ul className="list-inline mb-0 ">
              <li className="border-gredient-left p-2">
                <NewsArticleByline article={article} />
              </li>
            </ul>{' '}
            <ul className="list-inline mb-0">
              <li className="border-gredient-left p-2">
                <NewsArticleSourceLink article={article} />
              </li>
            </ul>
          </div>{' '}
          <div>
            <div dangerouslySetInnerHTML={{__html: article.body}}></div>
          </div>
        </main>
        <OtherNews articles={related} />
      </div>
    </>
  );
}

interface OtherNewsProps {
  articles: NewsArticle[];
}
function OtherNews({articles}: OtherNewsProps) {
  return (
    <div className="w-full max-w-full flex-shrink-0 lg:w-400">
      <h2 className="mb-14 text-2xl">
        <Trans message="Other news" />
      </h2>
      {articles.map(article => (
        <li key={article.id} className="d-flex align-items-center gap-4">
          <div className="img-holder">
            <NewsArticleImage
              article={article}
              className="mb-4 aspect-poster max-w-90"
            />
          </div>
          <div className="post-blog">
            <h6 className="line-clamp-2 text-sm font-semibold">
              <NewsArticleLink article={article} textClassName="text-base" />
            </h6>
            <ul className="list-inline mb-2">
              <li className="list-inline-item mb-0 border-0 pb-0 text-xs text-[var(--bs-body-color)]">
                <i
                  className="far fa-calendar-alt me-1 text-[var(--bs-primary)]"
                  aria-hidden="true"
                ></i>
                <FormattedDate date={article.created_at} />
              </li>
            </ul>
          </div>
        </li>
      ))}
    </div>
  );
}
